import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { QrCode, CheckCircle2, XCircle, AlertTriangle, Camera, CameraOff, Users, UserCheck, Mail, Download, Eye } from "lucide-react";
import { toast } from "sonner";

const READER_ID = "ihimm-qr-reader";

function parseTicket(text) {
  // Expected: IHIMM-TICKET|{ticket_id}|{event_id}
  if (!text) return null;
  const parts = text.split("|");
  if (parts[0] === "IHIMM-TICKET" && parts[1]) return { ticketId: parts[1], eventId: parts[2] || "" };
  return null;
}

export default function AdminScanner() {
  const scannerRef = useRef(null);
  const busyRef = useRef(false);
  const lastRef = useRef({ id: null, at: 0 });
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [count, setCount] = useState(0);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [attendance, setAttendance] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const downloadRoster = () => {
    if (!eventId) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/events/${eventId}/roster.csv`, "_blank");
  };

  const sendReminders = async () => {
    if (!eventId) return;
    setReminding(true);
    try {
      const { data } = await api.post(`/events/${eventId}/send-reminders`);
      toast.success(`Reminders sent to ${data.sent} ticket holder${data.sent === 1 ? "" : "s"}.`);
    } catch { toast.error("Could not send reminders"); }
    finally { setReminding(false); }
  };

  const previewReminder = async () => {
    if (!eventId) return;
    setPreviewing(true);
    try {
      const { data } = await api.post(`/events/${eventId}/reminder-preview`);
      toast.success(`Preview sent to ${data.sent_to}.`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not send preview"); }
    finally { setPreviewing(false); }
  };

  useEffect(() => {
    api.get("/events").then((r) => {
      const ticketed = r.data.filter((e) => e.ticketed && e.price > 0);
      setEvents(ticketed);
      if (ticketed.length && !eventId) setEventId(ticketed[0].id);
    }).catch(() => {});
  }, []);

  const loadAttendance = (id) => {
    if (!id) return;
    api.get(`/events/${id}/attendance`).then((r) => setAttendance(r.data)).catch(() => setAttendance(null));
  };

  useEffect(() => {
    if (!eventId) return;
    loadAttendance(eventId);
    const t = setInterval(() => loadAttendance(eventId), 6000);
    return () => clearInterval(t);
  }, [eventId]);

  const verify = async (ticketId) => {
    busyRef.current = true;
    try {
      const { data } = await api.get(`/tickets/verify/${ticketId}`);
      setResult({ ...data, ticket_id: ticketId, ts: Date.now() });
      if (data.valid) { setCount((c) => c + 1); loadAttendance(eventId); }
    } catch {
      setResult({ valid: false, ticket_id: ticketId, ts: Date.now() });
    } finally {
      setTimeout(() => { busyRef.current = false; }, 1600);
    }
  };

  const onScan = (decodedText) => {
    if (busyRef.current) return;
    const parsed = parseTicket(decodedText);
    if (!parsed) {
      setResult({ valid: false, unrecognized: true, ts: Date.now() });
      busyRef.current = true;
      setTimeout(() => { busyRef.current = false; }, 1400);
      return;
    }
    if (eventId && parsed.eventId && parsed.eventId !== eventId) {
      setResult({ valid: false, wrong_event: true, ts: Date.now() });
      busyRef.current = true;
      setTimeout(() => { busyRef.current = false; }, 1600);
      return;
    }
    const now = Date.now();
    if (lastRef.current.id === parsed.ticketId && now - lastRef.current.at < 2500) return;
    lastRef.current = { id: parsed.ticketId, at: now };
    verify(parsed.ticketId);
  };

  const start = async () => {
    setError("");
    try {
      const scanner = new Html5Qrcode(READER_ID, { verbose: false });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        onScan,
        () => {}
      );
      setScanning(true);
    } catch (e) {
      setError("Could not access the camera. Please grant camera permission and use HTTPS.");
      setScanning(false);
    }
  };

  const stop = async () => {
    const s = scannerRef.current;
    if (s) {
      try { await s.stop(); await s.clear(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => {
    return () => { const s = scannerRef.current; if (s) { s.stop().then(() => s.clear()).catch(() => {}); } };
  }, []);

  const r = result;
  const tone = r ? (r.wrong_event ? "wrongevent" : r.unrecognized ? "unknown" : r.valid ? (r.already_checked_in ? "warn" : "ok") : "bad") : null;

  return (
    <div className="min-h-screen bg-bone pt-32 pb-24" data-testid="admin-scanner">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-rust">Admin Console</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">Check-In Scanner</h1>
          <p className="font-sans text-charcoal/60 mt-4 max-w-xl">
            Point the camera at a guest's ticket QR code to confirm entry. Each scan is logged instantly.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
          {/* Camera */}
          <Reveal delay={0.05}>
            <section className="border border-charcoal/10 p-6" data-testid="scanner-camera">
              <div className="relative bg-charcoal/90 aspect-square overflow-hidden">
                <div id={READER_ID} className="w-full h-full [&_video]:object-cover [&_video]:w-full [&_video]:h-full" />
                {!scanning && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-bone/70">
                    <QrCode size={56} strokeWidth={1} />
                    <span className="font-mono text-xs uppercase tracking-widest">Camera off</span>
                  </div>
                )}
              </div>
              <div className="mt-5 flex items-center gap-3">
                {!scanning ? (
                  <button onClick={start} data-testid="scanner-start"
                    className="inline-flex items-center gap-2 bg-charcoal text-bone px-8 py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors">
                    <Camera size={15} /> Start Scanning
                  </button>
                ) : (
                  <button onClick={stop} data-testid="scanner-stop"
                    className="inline-flex items-center gap-2 border border-charcoal/30 px-8 py-3 font-mono text-xs uppercase tracking-widest hover:bg-charcoal hover:text-bone transition-colors">
                    <CameraOff size={15} /> Stop
                  </button>
                )}
                <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/50 ml-auto">
                  {count} checked in
                </span>
              </div>
              {error && <p className="mt-4 font-sans text-sm text-rust" data-testid="scanner-error">{error}</p>}
            </section>
          </Reveal>

          {/* Result */}
          <Reveal delay={0.1}>
            <section className="border border-charcoal/10 p-8 flex flex-col justify-center min-h-[360px]" data-testid="scanner-result">
              {!r && (
                <div className="text-center text-charcoal/40">
                  <QrCode size={44} strokeWidth={1} className="mx-auto" />
                  <p className="font-sans text-sm mt-4">Awaiting scan…</p>
                </div>
              )}
              {r && tone === "ok" && (
                <div className="text-center" data-testid="result-ok">
                  <CheckCircle2 size={64} strokeWidth={1.4} className="mx-auto text-olive" />
                  <h2 className="font-serif text-3xl tracking-tight mt-5 text-olive">Welcome In</h2>
                  <p className="font-serif text-xl mt-3">{r.user_name || "Guest"}</p>
                  <p className="font-sans text-sm text-charcoal/60 mt-1">{r.event_title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40 mt-2">{r.quantity} ticket{r.quantity === 1 ? "" : "s"}</p>
                </div>
              )}
              {r && tone === "warn" && (
                <div className="text-center" data-testid="result-warn">
                  <AlertTriangle size={64} strokeWidth={1.4} className="mx-auto text-gold" />
                  <h2 className="font-serif text-3xl tracking-tight mt-5 text-gold">Already Checked In</h2>
                  <p className="font-serif text-xl mt-3">{r.user_name || "Guest"}</p>
                  <p className="font-sans text-sm text-charcoal/60 mt-1">{r.event_title}</p>
                </div>
              )}
              {r && (tone === "bad" || tone === "unknown") && (
                <div className="text-center" data-testid="result-bad">
                  <XCircle size={64} strokeWidth={1.4} className="mx-auto text-rust" />
                  <h2 className="font-serif text-3xl tracking-tight mt-5 text-rust">Invalid Ticket</h2>
                  <p className="font-sans text-sm text-charcoal/60 mt-3">
                    {r.unrecognized ? "This code isn't a ministry ticket." : "No matching ticket was found."}
                  </p>
                </div>
              )}
              {r && tone === "wrongevent" && (
                <div className="text-center" data-testid="result-wrong-event">
                  <AlertTriangle size={64} strokeWidth={1.4} className="mx-auto text-rust" />
                  <h2 className="font-serif text-3xl tracking-tight mt-5 text-rust">Different Event</h2>
                  <p className="font-sans text-sm text-charcoal/60 mt-3">This ticket is for another event. Switch the event above to check them in.</p>
                </div>
              )}
            </section>
          </Reveal>
        </div>

        {/* Live Attendance */}
        <Reveal delay={0.15}>
          <section className="mt-8 border border-charcoal/10 p-8" data-testid="attendance-section">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Users size={20} className="text-navy" strokeWidth={1.5} />
                <h2 className="font-serif text-2xl tracking-tight">Live Attendance</h2>
              </div>
              <select value={eventId} onChange={(e) => setEventId(e.target.value)} data-testid="attendance-event-select"
                className="border border-charcoal/20 bg-bone px-4 py-2.5 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-navy">
                {events.length === 0 && <option value="">No ticketed events</option>}
                {events.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
              </select>
            </div>

            {attendance && (
              <>
                <div className="flex flex-wrap gap-3 mt-5">
                  <button onClick={sendReminders} disabled={reminding || attendance.total_tickets === 0} data-testid="send-reminders-btn"
                    className="inline-flex items-center gap-2 bg-navy text-bone px-5 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-charcoal transition-colors disabled:opacity-40">
                    <Mail size={14} /> {reminding ? "Sending..." : "Email Reminders"}
                  </button>
                  <button onClick={downloadRoster} disabled={attendance.total_tickets === 0} data-testid="download-roster-btn"
                    className="inline-flex items-center gap-2 border border-charcoal/30 px-5 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-charcoal hover:text-bone transition-colors disabled:opacity-40">
                    <Download size={14} /> Roster CSV
                  </button>
                  <button onClick={previewReminder} disabled={previewing || attendance.total_tickets === 0} data-testid="preview-reminder-btn"
                    className="inline-flex items-center gap-2 border border-charcoal/30 px-5 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-charcoal hover:text-bone transition-colors disabled:opacity-40">
                    <Eye size={14} /> {previewing ? "Sending..." : "Preview to Me"}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-px bg-charcoal/10 border border-charcoal/10 mt-6">
                  <div className="bg-bone p-6">
                    <div className="font-serif text-5xl text-olive" data-testid="attendance-checked">{attendance.checked_in}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-charcoal/50 mt-2">Checked In</div>
                  </div>
                  <div className="bg-bone p-6">
                    <div className="font-serif text-5xl text-charcoal/80" data-testid="attendance-total">{attendance.total_tickets}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-charcoal/50 mt-2">Tickets Sold</div>
                  </div>
                </div>
                <div className="mt-6 space-y-px bg-charcoal/10 border border-charcoal/10 max-h-[420px] overflow-y-auto">
                  {attendance.roster.length === 0 && <div className="bg-bone p-5 font-sans text-sm text-charcoal/50">No tickets sold for this event yet.</div>}
                  {attendance.roster.map((g) => (
                    <div key={g.id} className="bg-bone p-4 flex items-center justify-between gap-4" data-testid={`roster-${g.id}`}>
                      <div className="flex items-center gap-3">
                        {g.checked_in
                          ? <UserCheck size={18} className="text-olive shrink-0" strokeWidth={1.6} />
                          : <Users size={18} className="text-charcoal/30 shrink-0" strokeWidth={1.6} />}
                        <div>
                          <span className="font-serif text-lg tracking-tight">{g.user_name}</span>
                          <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40 ml-3">{g.quantity} ticket{g.quantity === 1 ? "" : "s"}</span>
                        </div>
                      </div>
                      {g.checked_in ? (
                        <span className="font-mono text-[10px] uppercase tracking-widest text-olive">In · {g.checked_in_at ? new Date(g.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                      ) : (
                        <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40">Awaiting</span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </Reveal>
      </div>
    </div>
  );
}
